import React, {useState, useEffect, Fragment, useContext } from 'react';
import { Stack, TableRow, Typography, Button } from '@mui/material';
import Subtitle from '../ui/Subtitle';
import Selector from '../ui/Selector';
import { backendFetch, backendPostWithJson } from '../../backend/backend';
import useStickyState from '../../utils/useStickyState';
import { tokenContext } from '../TokenProvider';
import { TSCell, TSTable } from '../ui/TableStructure';
import { useParams } from 'react-router-dom';
import dateFormatter from '../../utils/dateFormatter';

const EnterResultsPage = (props) => {
	const [fetched,setFetched]=useState(false);
	const [gameNumber,setGameNumber]=useState(1);
	const [date,setDate]=useState();
	const [validDates,setValidDates]=useState([]);
	const [activePlayers,setActivePlayers]=useState([]);
	const [selectedPlayers,setSelectedPlayers]=useStickyState([],"resultSelectedPlayers");
	const [selectedResults,setSelectedResults]=useStickyState([],"resultSelectedResults");
	const [update,setUpdate]=useState(false);
	const {token,dropAdmin}=useContext(tokenContext);
	const [requiredDate,setRequiredDate]=useState(null);
	const uniqueToken=useParams().token;

	const forceUpdate=()=>{setUpdate((update)=>!update);}

	useEffect(()=> {
		async function fetchData() {
			const nextGame=await backendFetch('games/next');
			const unplayedFixtures=await backendFetch('fixtures?season=active&played=false')
			const players=await backendFetch('players?active=true');
			setActivePlayers(players.map((p)=>p.name));
			setGameNumber(nextGame.gameNumber);
			setDate(nextGame.date);
			if (!props.token) {
				const dates=unplayedFixtures.map(f => f.date);
				if (!dates.includes(nextGame.date))
					dates.push(nextGame.date);
				dates.sort();
				setValidDates(dates);
			} else {
				if (uniqueToken)
				{
					setRequiredDate((await backendFetch('fixtures/date/'+uniqueToken)).date);
				}
			}
			setFetched(true);
		}
		fetchData();
	},[update,props.token,uniqueToken]);
	
	const clearSelections=()=>{
		setSelectedPlayers([]);
		setSelectedResults([]);
	}

	const selectSeat = (seat,player) => {
		setSelectedPlayers(players => {
			players[seat]=player;
			return [...players];
		});
	}

	const selectResults = (seat,result) => {
		setSelectedResults(results => {
			results[seat]=result;
			return[...results];
		});
	}

	const noShows=() => {
		var noShowsString="";
		activePlayers.forEach((p) => {
			if (!selectedPlayers.includes(p)) {
				if (noShowsString==="")
					noShowsString=p;
				else
					noShowsString+=(", "+p);
			}
		});
		return noShowsString;
	}

	const resultCells=(selectedPlayers) => activePlayers.map((_,i) => {
		const seatSelector = (i) =>
			<Selector
				onChange={(v)=>selectSeat(i,v)}
				value={selectedPlayers[i]===undefined?"":selectedPlayers[i]}
				key={i}
				disabled={i>0 && !selectedPlayers[i-1]}
				values={activePlayers.filter((p)=>!selectedPlayers.includes(p) || selectedPlayers[i]===p)}
				clearItem />
		
		const resultSelector = (i) =>
			<Selector
				onChange={(v) => selectResults(i,v)}
				value={selectedResults[i]===undefined?"":selectedResults[i]}
				key={i}
				disabled={!selectedPlayers[i]}
				values={selectedPlayers.filter(Boolean).map((_,j)=>j+1).filter((j)=>!selectedResults.includes(j) || selectedResults[i]===j) }
				ordinalValues
				clearItem />

		return(
			<TableRow key={i}>
				<TSCell>{i===0?'D':i+1}</TSCell>
				<TSCell>{seatSelector(i)}</TSCell>
				<TSCell>{resultSelector(i)}</TSCell>
			</TableRow>
		);
	});

	const validateResults = () => {
		if (selectedPlayers.filter(Boolean).length===0)
			return false;
		if (selectedPlayers.filter(Boolean).length!==selectedResults.filter(Boolean).length)
			return false;
		return true;
	}

	const verifyAndSubmitResult = async () => {
		var formEncoded=`date=${date}&game=${gameNumber}`;
		var success = await selectedPlayers.slice(0).reduce( async (prev,p,i) => {
			if (p.length>0) {
				formEncoded=formEncoded.concat(`&${p}=${selectedResults[i]}&${p}SeatPos=${i+1}`);
				return await prev && await backendPostWithJson(uniqueToken?uniqueToken:token,dropAdmin,`results/${date}/${gameNumber}/${p}`,{ position: selectedResults[i], seatPosition: i+1 });
			} else
				return await prev;
		},true);
		success = await activePlayers.slice(0).reduce(async (prev,p) => {
			if (!selectedPlayers.includes(p)) {
				formEncoded=formEncoded.concat(`&${p}=0&${p}SeatPos=0`);
				return await prev && await backendPostWithJson(uniqueToken?uniqueToken:token,dropAdmin,`results/${date}/${gameNumber}/${p}`,{ position: 0, seatPosition: 0 });
			} else
				return await prev;
		},success);
		if (success) {
			clearSelections();
			forceUpdate();
		}
	}

	return(
		<Stack spacing="8px">
			{requiredDate!==null && requiredDate<date &&
				<Subtitle text={`Results for ${dateFormatter(requiredDate)} have been entered`} />
			}
			{requiredDate!==null && requiredDate>date &&
				<Typography variant="h4">{`Warning: entering results for ${dateFormatter(requiredDate)} which aren't expected yet`}</Typography>
			}
			{fetched && (requiredDate===null || requiredDate>=date) &&
				<Fragment>
				<Subtitle text="Enter Results"/>
				{fetched && 
					<Fragment>
						{requiredDate===null &&
							<Stack direction="row">
								<Selector
									label="Date"
									onChange={setDate}
									value={date}
									values={validDates}
								/>
								<Selector
									label="Game"
									onChange={setGameNumber}
									value={gameNumber}
									values={[1,2]}
								/>
							</Stack>
						}
						{requiredDate!==null &&
							<Stack direction="row">
								<Typography variant="h5">Date:</Typography>
								<Typography variant="body1">{requiredDate}</Typography>
								<Typography variant="h5">Game:</Typography>
								<Typography variant="body1">{gameNumber}</Typography>
							</Stack>
						}
						<TSTable headings={["Seat","Player","Result"]} sx={{width:{sm:575}}}>
							{resultCells(selectedPlayers)}
						</TSTable>
						<Stack direction="row">
							<Typography variant="h5">No Shows:</Typography>
							<Typography variant="body1">{noShows()}</Typography>
						</Stack>
						<Button variant="contained" onClick={verifyAndSubmitResult} sx={{width:{sm:575}}}
							disabled={!validateResults()}>Submit</Button>
					</Fragment>
				}
				</Fragment>
			}
		</Stack>
	)
};

export default EnterResultsPage;
